import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CloseIcon from '@mui/icons-material/Close';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SimCardDownloadOutlinedIcon from '@mui/icons-material/SimCardDownloadOutlined';
import {
  Alert,
  Box,
  Card,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { saveAs } from 'file-saver';
import moment from 'moment';
import { useCallback, useEffect, useState } from 'react';
import React from 'react';
import { useGeneratePDF } from '@/hooks/useGeneratePDF';
import { toast } from 'react-toastify';

import { SWLoadingButton } from '@/components/LoadingButton';
import { SWLoadingButtonOutlined } from '@/components/SWLoadingButtonOutlined';
import { useBillingStatus } from '@/hooks/useBillingStatus';
import useMixPanel from '@/hooks/useMixPanel';

import { getDueDate } from '../dueDateUtils';
import {
  createStripeAccount,
  createStripeAccountLink,
  getUserInfo,
} from '@/pages/AccountSettings/api';
import { STRIPE_ACCOUNT_LINK_TYPE } from '@/pages/AccountSettings/types';
import { sendTenantReport } from '@/Structures/api';
import { useSubscribers } from '@/Structures/hooks/useSubscribers';
import { ISubscriber } from '@/Structures/subscribers/types';
import { StructureBilling } from '@/Structures/types';
import { Address, RateMethod } from '@/types';

import { convertArrayToCsvString, convertTimestampToTimeZone, naturalCompare } from '../../util';
import { formatNumber } from '../../util';
import {
  generateInvoicePDF,
  generateBatchCustomInvoices,
  getCustomInvoiceDownloadUrls,
  sendCustomInvoiceEmails,
  BatchCustomInvoiceInfo,
} from '../api';
import { PDFDocument } from 'pdf-lib';
import { useBills } from '../useBills';

interface StyleProps {
  isConfirm: boolean;
}

// style for the dialog paper/modal
const useStyles = makeStyles<Theme, StyleProps>((_theme) => ({
  paper: (props) => ({
    minHeight: props.isConfirm ? 'auto' : 560, // Auto height for confirm dialog
    maxHeight: '80vh',
    borderRadius: 10,
  }),
  dialogActionsRoot: {
    '@media print': {
      display: 'none',
    },
  },
}));

type Props = {
  bill: StructureBilling;
  onClose: () => void;
  open: boolean;
  structureName: string;
  autoDownload: 'csv' | 'pdf' | null;
  needReportButton: boolean;
  needSendBillButton: boolean;
  structureAddress: Address;
  timeZone: string;
  stripeConnect: boolean;
  invoices: any[];
  lineItems: any[];
  isPaymentReport: boolean;
  billsSentTimestamp: number | null;
  payOnlineUrl?: string | null;
  paymentInstructions?: string | null;
  dueDateDays?: number | null;
  onPaymentSettingsUpdate?: (
    payOnlineUrl: string | null,
    paymentInstructions: string | null,
    dueDateDays: number | null,
  ) => Promise<void>;
  onRefreshBills?: () => void;
};

interface IEventDetails {
  StartDate: string;
  EndDate: string;
  WaterUsage: number;
  VolumeUnit: string;
  Cost: number;
  RateMethod: RateMethod;
  PropertyName: string;
  PropertyID: string;
  BillID: string;
  EffectiveRate: number;
}

interface Device {
  serial_number: string;
  name: string;
  device_total_volume: number;
}

interface Unit {
  name: string;
  id: string;
  unit_total_volume: number;
  unit_cost: number;
  unit_cost_meter: number;
  devices: Device[];
  stripeLastStatus: string;
  stripeTotal: number;
  stripeCurrency: string;
  stripeInvoiceId: string;
  stripeInvoicePdfUrl: string;
  // TODO: Uncomment when stripe_invoice_url feature is ready for deployment
  // stripeInvoiceUrl: string;
  stripeCustomerId: string;
  unitId: string;
  unit_flags?: string[];
  is_billable?: boolean;
}

interface CommonAreaUsageData {
  name: string;
  unit_id: string;
  total_volume: number;
  split_volume: number;
  split_cost: number;
}

interface BillLineItem {
  id: string;
  type: string;
  description: string;
  cost?: number;
  cost_units?: string;
  volume?: number;
  volume_units?: string;
  rate_method?: string;
  effective_rate?: number;
  unit_id?: string; // snake_case from generateBill API
}

interface IUpdatedBillingData {
  billing_id: string;
  structure_id: string;
  structure_name: string;
  start_time: number;
  end_time: number;
  volume: number;
  volume_units: string;
  cost: number;
  cost_units: string;
  meter_total_volume: number;
  rate_method: string;
  effective_rate: number;
  units: Unit[];
  billable_units_count?: number;
  common_area_usage?: CommonAreaUsageData[];
  line_items?: BillLineItem[]; // Line items from generateBill API (snake_case)
}

type IStatusCount = {
  [key in 'not_created' | 'draft' | 'open' | 'paid' | 'uncollectible' | 'void']?: number;
};

export const Breakdown = ({
  bill,
  structureName,
  open,
  onClose,
  autoDownload,
  needReportButton,
  needSendBillButton,
  structureAddress,
  timeZone,
  stripeConnect,
  invoices,
  isPaymentReport,
  lineItems,
  billsSentTimestamp,
  payOnlineUrl,
  paymentInstructions,
  dueDateDays,
  onPaymentSettingsUpdate,
  onRefreshBills,
}: Props): JSX.Element => {
  dayjs.extend(utc);
  dayjs.extend(timezone);
  const { toPDF, targetRef } = useGeneratePDF({ filename: `bill_report.pdf` });
  const formattedStartDate = convertTimestampToTimeZone(bill.start_time, timeZone);
  const formattedEndDate = convertTimestampToTimeZone(bill.end_time, timeZone);
  const title = `Tenant Report`;
  const [loading, setLoading] = React.useState<boolean>(false);
  const [tz] = React.useState(timeZone === '' ? moment.tz.guess() : timeZone);
  const [isConfirmReport, setIsConfirmReport] = React.useState<boolean>(false);
  const { sendBills } = useBills(bill.structure_id);
  const [updatedBill, setUpdatedBill] = React.useState<IUpdatedBillingData>();
  const [alredyCSV, setAlredyCSV] = React.useState(false);
  const [openConfirmSendInvoices, setOpenConfirmSendInvoices] = useState(false);
  const [openConnectStripe, setOpenConnectStripe] = useState(false);
  const [loadingConnect, setLoadingConnect] = useState(false);
  const [downloadAnchorEl, setDownloadAnchorEl] = useState<null | HTMLElement>(null);
  const { subscribers } = useSubscribers(bill.structure_id);
  const classes = useStyles({ isConfirm: isConfirmReport });
  const [localInvoices, setLocalInvoices] = useState(invoices);

  // Batch custom invoice generation state
  const [openBatchGenerateDialog, setOpenBatchGenerateDialog] = useState(false);
  const [batchPayOnlineUrl, setBatchPayOnlineUrl] = useState(payOnlineUrl || '');
  const [batchPaymentInstructions, setBatchPaymentInstructions] = useState(
    paymentInstructions || '',
  );
  const [batchDueDateDays, setBatchDueDateDays] = useState<number>(dueDateDays ?? 21);
  const [batchSaveAsDefault, setBatchSaveAsDefault] = useState(false);
  // Track if user has modified the fields (to distinguish empty from "use default")
  const [batchUrlTouched, setBatchUrlTouched] = useState(false);
  const [batchInstructionsTouched, setBatchInstructionsTouched] = useState(false);
  const [isBatchGenerating, setIsBatchGenerating] = useState(false);
  const [batchCustomInvoices, setBatchCustomInvoices] = useState<BatchCustomInvoiceInfo[]>([]);
  const [batchGenerationStarted, setBatchGenerationStarted] = useState(false);
  const [batchGenerationProgress, setBatchGenerationProgress] = useState<{
    ready: number;
    total: number;
  } | null>(null);
  const [isPolling, setIsPolling] = useState(false);
  // Track when Stripe invoices are being sent (before PDF generation starts)
  const [isSendingStripeInvoices, setIsSendingStripeInvoices] = useState(false);
  // Local state to track when bills have been sent (overrides prop for immediate UI update)
  const [localBillsSentTimestamp, setLocalBillsSentTimestamp] = useState<number | null>(
    billsSentTimestamp,
  );
  // Use local state if set, otherwise fall back to prop
  const effectiveBillsSentTimestamp = localBillsSentTimestamp ?? billsSentTimestamp;

  // Track which invoices have pending custom invoice records (not_created status)
  // This is used to disable individual generate buttons when batch generation is in progress
  const [pendingCustomInvoiceIds, setPendingCustomInvoiceIds] = useState<Set<string>>(new Set());

  // Org name for PDF filenames
  const [orgName, setOrgName] = useState('');
  React.useEffect(() => {
    getUserInfo().then((info) => setOrgName(info.user.organization_name));
  }, []);

  // Track actual due dates for custom invoices (keyed by billing_invoice_id)
  // This stores the real due date used when the invoice was generated
  const [customInvoiceDueDates, setCustomInvoiceDueDates] = useState<Map<string, string>>(
    new Map(),
  );

  // Selection state for batch download
  const [selectedInvoiceIds, setSelectedInvoiceIds] = useState<Set<string>>(new Set());

  // Send invoices by email state
  const [openSendEmailDialog, setOpenSendEmailDialog] = useState(false);
  const [isSendingEmails, setIsSendingEmails] = useState(false);
  // Track email-sent timestamps from DB (keyed by billing_invoice_id -> epoch seconds)
  const [emailSentMap, setEmailSentMap] = useState<Map<string, number>>(new Map());

  // Sync local invoices with prop changes and auto-select all for non-Stripe
  React.useEffect(() => {
    setLocalInvoices(invoices);
    // Auto-select all invoices for non-Stripe accounts when invoices are loaded
    if (!stripeConnect && invoices.length > 0) {
      const allInvoiceIds = new Set(invoices.map((inv) => inv.id).filter(Boolean) as string[]);
      setSelectedInvoiceIds(allInvoiceIds);
    }
  }, [invoices, stripeConnect]);

  // Sync local billsSentTimestamp with prop changes (e.g., parent data refresh)
  React.useEffect(() => {
    if (billsSentTimestamp) {
      setLocalBillsSentTimestamp(billsSentTimestamp);
    }
  }, [billsSentTimestamp]);

  // Check for pending custom invoices when dialog opens or invoices change
  // This helps detect custom invoices that were started in a previous session
  // Also fetches due dates for all invoices (even those with PDFs already)
  React.useEffect(() => {
    // Skip if dialog is closed, no billing ID, no invoices, or already tracking batch generation
    if (!open || !bill?.billing_id || invoices.length === 0) {
      return;
    }

    // If batch generation is already in progress, the polling effect handles status
    // Don't query and overwrite pendingCustomInvoiceIds
    if (batchGenerationStarted) {
      return;
    }

    // Get ALL invoice IDs to fetch due dates (component remounts on dialog reopen, losing state)
    const allInvoiceIds = invoices.filter((inv) => inv.id).map((inv) => inv.id);

    if (allInvoiceIds.length === 0) {
      setPendingCustomInvoiceIds(new Set());
      return;
    }

    // Query the backend to check custom invoice status and get due dates for ALL invoices
    getCustomInvoiceDownloadUrls(bill.billing_id, allInvoiceIds)
      .then((result) => {
        // Find invoices that have custom invoice records but PDF not ready yet
        const pendingIds = new Set(
          result.custom_invoices
            .filter((ci) => ci.status === 'not_created')
            .map((ci) => ci.billing_invoice_id),
        );
        setPendingCustomInvoiceIds(pendingIds);

        // Store due dates for custom invoices that have them
        const dueDates = new Map<string, string>();
        result.custom_invoices.forEach((ci) => {
          if (ci.due_date) {
            dueDates.set(ci.billing_invoice_id, ci.due_date);
          }
        });
        if (dueDates.size > 0) {
          setCustomInvoiceDueDates((prev) => {
            const merged = new Map(prev);
            dueDates.forEach((value, key) => merged.set(key, value));
            return merged;
          });
        }

        // Store email-sent timestamps
        const emailSent = new Map<string, number>();
        result.custom_invoices.forEach((ci) => {
          if (ci.email_sent_at) {
            emailSent.set(ci.billing_invoice_id, ci.email_sent_at);
          }
        });
        if (emailSent.size > 0) {
          setEmailSentMap((prev) => {
            const merged = new Map(prev);
            emailSent.forEach((value, key) => merged.set(key, value));
            return merged;
          });
        }

        // If there are pending invoices, resume polling
        if (pendingIds.size > 0) {
          // Set up state to trigger the polling effect
          const pendingInvoices: BatchCustomInvoiceInfo[] = result.custom_invoices
            .filter((ci) => ci.status === 'not_created' && ci.id)
            .map((ci) => ({
              custom_invoice_id: ci.id!,
              billing_invoice_id: ci.billing_invoice_id,
              status: 'not_created' as const,
            }));
          if (pendingInvoices.length > 0) {
            setBatchCustomInvoices(pendingInvoices);
            setBatchGenerationStarted(true);
          }
        }
      })
      .catch(() => {
        // Silently handle errors - this is a best-effort check
      });
  }, [open, bill?.billing_id, invoices, batchGenerationStarted]);

  // Sync batch payment settings with prop changes
  React.useEffect(() => {
    setBatchPayOnlineUrl(payOnlineUrl || '');
    setBatchPaymentInstructions(paymentInstructions || '');
    setBatchDueDateDays(dueDateDays ?? 21);
  }, [payOnlineUrl, paymentInstructions, dueDateDays]);

  // Ref to track if generation has completed (prevents duplicate toasts)
  const generationCompletedRef = React.useRef(false);

  // Store onRefreshBills in a ref to avoid dependency issues
  const onRefreshBillsRef = React.useRef(onRefreshBills);
  React.useEffect(() => {
    onRefreshBillsRef.current = onRefreshBills;
  }, [onRefreshBills]);

  // Poll for batch generation progress after generation starts
  React.useEffect(() => {
    if (!batchGenerationStarted || batchCustomInvoices.length === 0 || !bill?.billing_id) {
      return;
    }

    // Reset completion flag when starting new generation
    generationCompletedRef.current = false;

    // Get billing_invoice_ids from the batch response
    const billingInvoiceIds = batchCustomInvoices.map((inv) => inv.billing_invoice_id);
    if (billingInvoiceIds.length === 0) return;

    setIsPolling(true);
    setBatchGenerationProgress({ ready: 0, total: billingInvoiceIds.length });

    const pollInterval = setInterval(async () => {
      // Skip if already completed
      if (generationCompletedRef.current) {
        clearInterval(pollInterval);
        return;
      }

      try {
        const result = await getCustomInvoiceDownloadUrls(bill.billing_id, billingInvoiceIds);
        const readyCount = result.ready_count;
        const totalCount = billingInvoiceIds.length;

        setBatchGenerationProgress({ ready: readyCount, total: totalCount });

        // Store due dates for custom invoices that have them
        const dueDates = new Map<string, string>();
        result.custom_invoices.forEach((ci) => {
          if (ci.due_date) {
            dueDates.set(ci.billing_invoice_id, ci.due_date);
          }
        });
        if (dueDates.size > 0) {
          setCustomInvoiceDueDates((prev) => {
            const merged = new Map(prev);
            dueDates.forEach((value, key) => merged.set(key, value));
            return merged;
          });
        }

        // Store email-sent timestamps
        const emailSent = new Map<string, number>();
        result.custom_invoices.forEach((ci) => {
          if (ci.email_sent_at) {
            emailSent.set(ci.billing_invoice_id, ci.email_sent_at);
          }
        });
        if (emailSent.size > 0) {
          setEmailSentMap((prev) => {
            const merged = new Map(prev);
            emailSent.forEach((value, key) => merged.set(key, value));
            return merged;
          });
        }

        // Stop polling when all are ready
        if (readyCount >= totalCount && !generationCompletedRef.current) {
          generationCompletedRef.current = true;
          clearInterval(pollInterval);
          setIsPolling(false);
          // Clear pending set since all are now ready
          setPendingCustomInvoiceIds(new Set());
          toast.success('All SSW invoice PDFs are ready!', { closeButton: true });
          // Refresh bills data so the invoices show up in the table
          if (onRefreshBillsRef.current) {
            onRefreshBillsRef.current();
          }
        }
      } catch (error) {
        console.error('Error polling for invoice status:', error);
        // Don't stop polling on error, just log it
      }
    }, 3000); // Poll every 3 seconds

    return () => {
      clearInterval(pollInterval);
      setIsPolling(false);
    };
  }, [batchGenerationStarted, batchCustomInvoices, bill?.billing_id]);

  // Callback to update invoice's customPdfUrl after generation
  const handleInvoicePdfGenerated = React.useCallback((invoiceId: string, pdfUrl: string) => {
    setLocalInvoices((prev) =>
      prev.map((inv) => (inv.id === invoiceId ? { ...inv, customInvoicePdfUrl: pdfUrl } : inv)),
    );
  }, []);

  // Single source of truth: subscriber lookup by ID.
  // Used by both the breakdown table rows and the email confirmation dialog.
  const subscriberById = React.useMemo(() => {
    return new Map(
      subscribers
        .filter((sub): sub is ISubscriber & { id: string } => !!sub.id)
        .map((sub) => [sub.id, sub]),
    );
  }, [subscribers]);

  // Build rows for the email confirmation table from selected invoices
  const selectedEmailRows = React.useMemo(() => {
    const unitMap = new Map<string, string>();
    (bill.units || []).forEach((unit) => {
      unitMap.set(unit.id, unit.name);
    });

    return localInvoices
      .filter((inv) => selectedInvoiceIds.has(inv.id))
      .map((inv) => {
        const unitName = unitMap.get(inv.unitId) || inv.unitId || '';
        const sub = inv.subscriberId ? subscriberById.get(inv.subscriberId) : undefined;
        return {
          unitName,
          fullName: sub ? `${sub.first_name} ${sub.last_name}` : '—',
          email: sub?.email || '—',
        };
      })
      .sort((a, b) => naturalCompare(a.unitName, b.unitName));
  }, [selectedInvoiceIds, localInvoices, subscriberById, bill.units]);

  const alreadySentCount = React.useMemo(() => {
    return Array.from(selectedInvoiceIds).filter((id) => emailSentMap.has(id)).length;
  }, [selectedInvoiceIds, emailSentMap]);

  const {
    billingEnabled,
    stripeAccountExists,
    stripeSetupComplete,
    loading: billingStatusLoading,
  } = useBillingStatus();

  // run your merge whenever bill or localInvoices change
  // bill.line_items is used internally, so no need for separate lineItems dependency
  React.useEffect(() => {
    const enriched = mergeInvoicesToUnits(bill, localInvoices);
    setUpdatedBill(enriched);
  }, [bill, localInvoices]);

  const isDownloadMenuOpen = Boolean(downloadAnchorEl);

  const [eventDetails] = React.useState<IEventDetails>({
    StartDate: formattedStartDate,
    EndDate: formattedEndDate,
    WaterUsage: bill.volume,
    VolumeUnit: bill.volume_units,
    Cost: bill.cost,
    RateMethod: bill.rate_method,
    PropertyName: structureName,
    PropertyID: bill.structure_id,
    BillID: bill.billing_id,
    EffectiveRate: bill.effective_rate,
  });
  const { trackEvent } = useMixPanel();
  const mergeInvoicesToUnits = (bill: StructureBilling, invoices: any[]): IUpdatedBillingData => {
    // start by telling TS "I know this is IUpdatedBillingData"
    const updatedBill = { ...bill } as IUpdatedBillingData;

    // Use line_items from the bill (generateBill API) - snake_case
    // This ensures consistency since bill.units also comes from generateBill
    const billLineItems = bill.line_items ?? [];
    const splitItems = billLineItems.filter((item) => item.type === 'SPLIT');
    const flatItems = billLineItems.filter((item) => item.type === 'FLAT');
    const unitFlatItems = billLineItems.filter((item) => item.type === 'UNIT_FLAT');

    // Use billable_units_count from API if available, otherwise fallback to total units
    const billableUnitsCount = bill.billable_units_count ?? updatedBill.units.length;

    // Calculate total common area cost per billable unit (split among billable units)
    const commonAreaUsage = bill.common_area_usage ?? [];
    const commonAreaCostPerUnit = commonAreaUsage.reduce((sum, ca) => sum + ca.split_cost, 0);

    updatedBill.units = updatedBill.units.map((unit) => {
      let unitCost = 0;

      // Only add common area cost, split fees, and flat fees to billable units
      // Non-billable and common area units don't get these charges
      const isBillable = unit.is_billable ?? true; // Default to true for backwards compatibility

      if (isBillable) {
        // Add common area usage cost (already split per billable unit from API)
        unitCost += commonAreaCostPerUnit;

        splitItems.forEach((item) => {
          unitCost += (item.cost ?? 0) / billableUnitsCount;
        });

        flatItems.forEach((item) => {
          unitCost += item.cost ?? 0;
        });
      }

      // Unit flat fees are always for specific units
      // line_items come from generateBill API (snake_case) - same source as bill.units
      unitFlatItems.forEach((item) => {
        if (item.unit_id === unit.id) {
          unitCost += item.cost ?? 0;
        }
      });

      return { ...unit, unit_cost: unitCost };
    });

    // Calculate total cost = original water bill + fees for billable units
    // Start with original bill.cost (the actual water utility bill amount)
    // Then add fees that apply to billable units only
    const totalFees = updatedBill.units.reduce((acc, unit: any) => {
      const isBillable = unit.is_billable ?? true;
      if (!isBillable) return acc;
      // unit_cost includes: common area split + split fees + flat fees + unit flat fees
      // Subtract common area split to get just the fees
      const feesOnly = (unit.unit_cost || 0) - commonAreaCostPerUnit;
      return acc + feesOnly;
    }, 0);
    updatedBill.cost = bill.cost + totalFees;

    if (invoices && invoices.length > 0) {
      updatedBill.units = updatedBill.units.map((unit) => {
        // invoices come from bills API which converts to camelCase
        const matchingInvoice = invoices.find((invoice) => invoice.unitId === unit.id);
        if (matchingInvoice) {
          // Destructure to avoid overwriting unit.id with invoice.id
          const { id: _invoiceId, ...invoiceWithoutId } = matchingInvoice;
          return { ...unit, ...invoiceWithoutId };
        }
        return unit;
      });
    }

    return updatedBill;
  };

  const handleDownloadMenuOpen = (e: React.MouseEvent<HTMLButtonElement>) => {
    setDownloadAnchorEl(e.currentTarget);
  };
  const handleDownloadMenuClose = () => {
    setDownloadAnchorEl(null);
  };

  const handleDownloadPDF = () => {
    toPDF();
    trackEvent('Bill Downloaded', eventDetails);
    handleDownloadMenuClose();
  };

  const handleDownloadCSVMenu = () => {
    handleCsvDownload();
    trackEvent('Bill Downloaded', eventDetails);
    handleDownloadMenuClose();
  };

  const handleSendInvoices = async () => {
    // Close the confirmation sub-dialog
    setOpenConfirmSendInvoices(false);
    // Set loading state - the useEffect below will open the batch dialog
    setIsSendingStripeInvoices(true);
  };

  // Effect to open batch dialog and start sending when isSendingStripeInvoices becomes true
  useEffect(() => {
    if (isSendingStripeInvoices && !openBatchGenerateDialog) {
      setOpenBatchGenerateDialog(true);
      // Start the send process
      handleSendBill();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSendingStripeInvoices]);

  const handleCsvDownload = useCallback(async () => {
    // 1) merge the invoices into the bill (uses bill.line_items internally)
    const enrichedBill = mergeInvoicesToUnits(bill, localInvoices);

    // 2) pick enrichedBill (since updatedBill may not be set)
    const data = enrichedBill;

    // 3) Organize fees by type for consistent columns - use bill.line_items (snake_case)
    const billLineItems = bill.line_items ?? [];
    const splitFees = billLineItems.filter((item) => item.type === 'SPLIT');
    const flatFees = billLineItems.filter((item) => item.type === 'FLAT');
    const unitFlatFees = billLineItems.filter((item) => item.type === 'UNIT_FLAT');
    const hasAnyFees = splitFees.length + flatFees.length + unitFlatFees.length > 0;
    // Use billable_units_count from API if available, otherwise fallback to total units
    const billableUnitsCount = bill.billable_units_count ?? data.units.length;

    // 4) sort units alphanumerically and build CSV rows with water cost, itemized fees, and total
    const sortedUnits = [...data.units].sort((a, b) => naturalCompare(a.name, b.name));
    const rows = sortedUnits.map((unit) => {
      const baseRow: any = {
        'Unit Name': unit.name,
        'Structure Name': bill.structure_name,
        Volume: formatNumber(unit.unit_total_volume, 3),
        'Volume Units': bill.volume_units,
        'Effective Rate': formatNumber(bill.effective_rate, 2),
        'Total Cost': `$${formatNumber((unit.unit_cost || 0) + (unit.unit_cost_meter || 0), 2)}`,
        'Cost Units': bill.cost_units,
        'Water Usage Cost': `$${formatNumber(unit.unit_cost_meter || 0, 2)}`,
      };

      // Add fee columns if fees exist
      if (hasAnyFees) {
        let feeIndex = 1;

        // Add SPLIT fees (divided by number of billable units)
        splitFees.forEach((fee) => {
          const feeAmount = (fee.cost ?? 0) / billableUnitsCount;
          baseRow[`Fee ${feeIndex} Name`] = fee.description || 'Split Fee';
          baseRow[`Fee ${feeIndex} Type`] = 'SPLIT';
          baseRow[`Fee ${feeIndex} Cost`] = `$${formatNumber(feeAmount, 2)}`;
          feeIndex++;
        });

        // Add FLAT fees (applied to all units)
        flatFees.forEach((fee) => {
          baseRow[`Fee ${feeIndex} Name`] = fee.description || 'Flat Fee';
          baseRow[`Fee ${feeIndex} Type`] = 'FLAT';
          baseRow[`Fee ${feeIndex} Cost`] = `$${formatNumber(fee.cost ?? 0, 2)}`;
          feeIndex++;
        });

        // Add UNIT_FLAT fees (only for specific units)
        unitFlatFees.forEach((fee) => {
          // line_items come from generateBill API (snake_case)
          if (fee.unit_id === unit.id) {
            baseRow[`Fee ${feeIndex} Name`] = fee.description || 'Unit-Specific Fee';
            baseRow[`Fee ${feeIndex} Type`] = 'UNIT_FLAT';
            baseRow[`Fee ${feeIndex} Cost`] = `$${formatNumber(fee.cost ?? 0, 2)}`;
            feeIndex++;
          }
        });
      }

      // Add Stripe fields if connected (after all fees)
      if (stripeConnect) {
        baseRow['Status'] = unit.stripeLastStatus;
        baseRow['Invoice URL'] = unit.stripeInvoicePdfUrl;
      }

      return baseRow;
    });

    // 5) if not on Stripe, drop Status & Invoice URL
    const finalRows = stripeConnect
      ? rows
      : rows.map(({ Status: _Status, 'Invoice URL': _InvoiceURL, ...keep }) => keep);

    const csv = await convertArrayToCsvString(finalRows);
    saveAs(
      new Blob([csv], { type: 'text/plain;charset=utf-8' }),
      `${title.replace(/ /g, '_')}.csv`,
    );
  }, [bill, localInvoices, stripeConnect, title]);

  useEffect(() => {
    const handleAction = async () => {
      if (autoDownload === 'pdf') return toPDF();

      if (!alredyCSV && open && autoDownload === 'csv' && bill && updatedBill) {
        setAlredyCSV(true);
        try {
          await handleCsvDownload();
          await trackEvent('Bill Downloaded', eventDetails);
          onClose();
        } catch (error) {
          console.error('Error during CSV download:', error);
        }
      }
    };

    handleAction();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    open,
    autoDownload,
    alredyCSV,
    handleCsvDownload,
    trackEvent,
    eventDetails,
    onClose,
    bill,
    updatedBill,
  ]);

  const handleSendReport = async () => {
    setLoading(true);
    await sendTenantReport(bill.billing_id);
    await trackEvent('Subscriber Usage Report Send', {
      PropertyID: eventDetails.PropertyID,
      PropertyName: eventDetails.PropertyName,
      BillID: eventDetails.BillID,
    });
    setLoading(false);
    onClose();
  };

  const handleSendBill = async () => {
    setLoading(true);
    try {
      // If "Save as default" is checked, update property settings first
      if (batchSaveAsDefault && onPaymentSettingsUpdate) {
        await onPaymentSettingsUpdate(
          batchPayOnlineUrl.trim() || null,
          batchPaymentInstructions.trim() || null,
          batchDueDateDays,
        );
        toast.success('Payment settings saved as default!', { closeButton: true });
      }

      await trackEvent('Invoice Sent', {
        PropertyName: bill.structure_name,
        PropertyID: bill.structure_id,
        StartDate: convertTimestampToTimeZone(bill.start_time, timeZone),
        EndDate: convertTimestampToTimeZone(bill.end_time, timeZone),
      });

      // 1) Send Stripe invoices
      await sendBills(bill.billing_id, batchDueDateDays);
      // Update local timestamp so UI immediately reflects bills have been sent
      setLocalBillsSentTimestamp(Math.floor(Date.now() / 1000));
      toast.success('Stripe invoices sent to tenants!', { closeButton: true });

      // 2) Generate SSW invoice PDFs with the same settings
      const options: {
        pay_online_url?: string;
        payment_instructions?: string;
        due_date_days?: number;
      } = {
        due_date_days: batchDueDateDays,
      };
      if (batchPayOnlineUrl.trim()) {
        options.pay_online_url = batchPayOnlineUrl.trim();
      }
      if (batchPaymentInstructions.trim()) {
        options.payment_instructions = batchPaymentInstructions.trim();
      }

      const result = await generateBatchCustomInvoices(bill.billing_id, options);
      setBatchCustomInvoices(result.custom_invoices);
      // Mark these invoices as pending so individual generate buttons are disabled
      const pendingIds = new Set(result.custom_invoices.map((ci) => ci.billing_invoice_id));
      setPendingCustomInvoiceIds(pendingIds);
      // Clear Stripe sending state and start batch generation together to avoid showing form
      setIsSendingStripeInvoices(false);
      setBatchGenerationStarted(true);

      await trackEvent('Batch Custom Invoices Generated', {
        PropertyName: bill.structure_name,
        PropertyID: bill.structure_id,
        BillID: bill.billing_id,
        TotalCount: result.total_queued,
      });

      toast.success(`Invoices sent! ${result.total_queued} SSW invoice PDFs are being generated.`, {
        closeButton: true,
      });
    } catch (error) {
      console.error('Failed to send invoices:', error);
      toast.error('Failed to send invoices. Please try again.', { closeButton: true });
      setIsSendingStripeInvoices(false);
    } finally {
      setLoading(false);
    }
  };

  // Handler for opening Stripe send invoices dialog
  const handleOpenSendInvoicesDialog = () => {
    setBatchPayOnlineUrl(payOnlineUrl || '');
    setBatchPaymentInstructions(paymentInstructions || '');
    setBatchDueDateDays(dueDateDays ?? 21);
    setOpenConfirmSendInvoices(true);
  };

  // Handler for opening batch generate dialog
  const handleOpenBatchGenerateDialog = () => {
    setBatchPayOnlineUrl(payOnlineUrl || '');
    setBatchPaymentInstructions(paymentInstructions || '');
    setBatchDueDateDays(dueDateDays ?? 21);
    setBatchSaveAsDefault(false);
    setBatchUrlTouched(false);
    setBatchInstructionsTouched(false);
    setBatchGenerationStarted(false);
    setBatchCustomInvoices([]);
    setOpenBatchGenerateDialog(true);
  };

  // Handler for batch custom invoice generation
  const handleBatchGenerate = async () => {
    setIsBatchGenerating(true);

    try {
      // If "Save as default" is checked, update property settings first
      if (batchSaveAsDefault && onPaymentSettingsUpdate) {
        await onPaymentSettingsUpdate(
          batchPayOnlineUrl.trim() || null,
          batchPaymentInstructions.trim() || null,
          batchDueDateDays,
        );
        toast.success('Payment settings saved as default!', { closeButton: true });
      }

      // Prepare options
      // If user touched a field, always send it (even if empty) to override defaults
      // If user didn't touch, don't send it so backend can use property defaults
      const options: {
        pay_online_url?: string;
        payment_instructions?: string;
        due_date_days?: number;
      } = {};
      if (batchUrlTouched || batchPayOnlineUrl.trim()) {
        options.pay_online_url = batchPayOnlineUrl.trim();
      }
      if (batchInstructionsTouched || batchPaymentInstructions.trim()) {
        options.payment_instructions = batchPaymentInstructions.trim();
      }
      // Only pass due_date_days for non-Stripe users
      if (!stripeConnect) {
        options.due_date_days = batchDueDateDays;
      }

      // Trigger batch generation
      const result = await generateBatchCustomInvoices(bill.billing_id, options);
      setBatchCustomInvoices(result.custom_invoices);
      setBatchGenerationStarted(true);

      await trackEvent('Batch Custom Invoices Generated', {
        PropertyName: bill.structure_name,
        PropertyID: bill.structure_id,
        BillID: bill.billing_id,
        TotalCount: result.total_queued,
      });

      toast.success(
        `Started generating ${result.total_queued} SSW invoice PDFs. This may take a moment.`,
        {
          closeButton: true,
        },
      );
    } catch (error) {
      console.error('Failed to generate batch custom invoices:', error);
      // Error toast is handled in API function
    } finally {
      setIsBatchGenerating(false);
    }
  };

  // Handler for closing batch generate dialog
  const handleCloseBatchGenerateDialog = () => {
    setOpenBatchGenerateDialog(false);
    setBatchGenerationStarted(false);
    setBatchCustomInvoices([]);
    setBatchGenerationProgress(null);
    setIsPolling(false);
    setIsSendingStripeInvoices(false);
    generationCompletedRef.current = false;
  };

  // Helpers for PDF filenames
  const sanitize = (s: string) => s.replace(/[^a-zA-Z0-9]/g, '_');
  const formatDateForFilename = (timestamp: number) => {
    const d = moment.unix(timestamp);
    if (timeZone) d.tz(timeZone);
    return d.format('MM-DD-YYYY');
  };

  // Handler for downloading selected invoices - merges PDFs using pdf-lib
  const handleDownloadSelected = async () => {
    if (selectedInvoiceIds.size === 0) return;

    setLoading(true);
    try {
      // Get download URLs for selected invoices
      const result = await getCustomInvoiceDownloadUrls(
        bill.billing_id,
        Array.from(selectedInvoiceIds),
      );

      // Check if all selected invoices are ready
      if (result.not_ready_count > 0) {
        toast.warning(
          `${result.not_ready_count} invoice(s) are still being generated. Please wait and try again.`,
          { closeButton: true },
        );
        setLoading(false);
        return;
      }

      const readyUrls = result.custom_invoices.filter((d) => d.download_url);

      // Sort by unit name alphanumerically so merged PDF pages are in order
      const unitMap = new Map(bill.units.map((u) => [u.id, u.name]));
      readyUrls.sort((a, b) => {
        const invA = invoices.find((inv) => inv.id === a.billing_invoice_id);
        const invB = invoices.find((inv) => inv.id === b.billing_invoice_id);
        const nameA = (invA ? unitMap.get(invA.unitId) : '') || '';
        const nameB = (invB ? unitMap.get(invB.unitId) : '') || '';
        return naturalCompare(nameA, nameB);
      });

      if (readyUrls.length === 0) {
        toast.error('No invoices are ready for download.', { closeButton: true });
        setLoading(false);
        return;
      }

      if (readyUrls.length === 1) {
        // Single PDF - download with descriptive filename
        const matchingInvoice = invoices.find((inv) => inv.id === readyUrls[0].billing_invoice_id);
        const matchingUnit = bill.units.find((u) => u.id === matchingInvoice?.unitId);
        const unitName = matchingUnit?.name || 'Invoice';

        const response = await fetch(readyUrls[0].download_url!);
        const pdfBytes = await response.arrayBuffer();
        const blob = new Blob([pdfBytes], { type: 'application/pdf' });
        const blobUrl = URL.createObjectURL(blob);

        const startDate = formatDateForFilename(bill.start_time);
        const endDate = formatDateForFilename(bill.end_time);
        const filename = `${sanitize(orgName)}_${sanitize(bill.structure_name)}_${sanitize(
          unitName,
        )}_${startDate}_to_${endDate}.pdf`;

        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(blobUrl);
      } else {
        // Multiple PDFs - merge using pdf-lib
        toast.info(`Merging ${readyUrls.length} invoices into a single PDF...`, {
          closeButton: true,
        });

        try {
          // Create a new PDF document to merge into
          const mergedPdf = await PDFDocument.create();

          // Fetch and merge each PDF
          for (const urlInfo of readyUrls) {
            try {
              const response = await fetch(urlInfo.download_url!);
              const pdfBytes = await response.arrayBuffer();
              const pdfDoc = await PDFDocument.load(pdfBytes);
              const copiedPages = await mergedPdf.copyPages(pdfDoc, pdfDoc.getPageIndices());
              copiedPages.forEach((page) => mergedPdf.addPage(page));
            } catch (err) {
              console.error(
                `Failed to fetch/merge PDF for invoice ${urlInfo.billing_invoice_id}:`,
                err,
              );
              // Continue with other PDFs even if one fails
            }
          }

          // Save the merged PDF
          const mergedPdfBytes = await mergedPdf.save();

          // Create a blob and download link
          const blob = new Blob([mergedPdfBytes as BlobPart], { type: 'application/pdf' });
          const url = URL.createObjectURL(blob);

          // Create download filename with date range
          const startDate = formatDateForFilename(bill.start_time);
          const endDate = formatDateForFilename(bill.end_time);
          const filename = `${sanitize(orgName)}_${sanitize(
            bill.structure_name,
          )}_${startDate}_to_${endDate}.pdf`;

          // Trigger download
          const link = document.createElement('a');
          link.href = url;
          link.download = filename;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);

          // Clean up the object URL
          URL.revokeObjectURL(url);

          toast.success(`Successfully merged ${readyUrls.length} invoices!`, { closeButton: true });
        } catch (mergeError) {
          console.error('Failed to merge PDFs:', mergeError);
          toast.error('Failed to merge PDFs. Please try downloading individually.', {
            closeButton: true,
          });
        }
      }

      await trackEvent('Batch Custom Invoices Downloaded', {
        PropertyName: bill.structure_name,
        PropertyID: bill.structure_id,
        BillID: bill.billing_id,
        SelectedCount: selectedInvoiceIds.size,
        ReadyCount: result.ready_count,
      });
    } catch (error) {
      console.error('Failed to download selected invoices:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSendInvoiceEmails = async () => {
    setIsSendingEmails(true);
    try {
      const result = await sendCustomInvoiceEmails(bill.billing_id, Array.from(selectedInvoiceIds));
      setOpenSendEmailDialog(false);

      // Optimistic update: immediately populate emailSentMap so column + warning update instantly
      const now = Math.floor(Date.now() / 1000);
      setEmailSentMap((prev) => {
        const updated = new Map(prev);
        selectedInvoiceIds.forEach((id) => updated.set(id, now));
        return updated;
      });

      if (result.skipped > 0) {
        toast.warning(`${result.total_queued} email(s) queued. ${result.skipped} skipped.`, {
          closeButton: true,
        });
      } else {
        toast.success(`${result.total_queued} invoice email(s) queued for sending!`, {
          closeButton: true,
        });
      }

      // Refetch to get updated email_sent_at from DB
      if (bill?.billing_id) {
        const allInvoiceIds = invoices.filter((inv) => inv.id).map((inv) => inv.id);
        if (allInvoiceIds.length > 0) {
          const refreshResult = await getCustomInvoiceDownloadUrls(bill.billing_id, allInvoiceIds);
          const emailSent = new Map<string, number>();
          refreshResult.custom_invoices.forEach((ci) => {
            if (ci.email_sent_at) {
              emailSent.set(ci.billing_invoice_id, ci.email_sent_at);
            }
          });
          setEmailSentMap(emailSent);
        }
      }
    } catch (error) {
      // API function already shows toast error
    } finally {
      setIsSendingEmails(false);
    }
  };

  // 1) one single source of truth for invoice‐status counts
  const statusCount = React.useMemo<IStatusCount>(() => {
    return localInvoices.reduce((acc, inv) => {
      const s = inv.stripeLastStatus || 'unknown';
      acc[s] = (acc[s] || 0) + 1;
      return acc;
    }, {} as IStatusCount);
  }, [localInvoices]);

  // 2) total = number of prorated‐segments = localInvoices.length
  const totalInvoices = localInvoices.length;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      scroll="body"
      aria-labelledby="form-dialog-title"
      maxWidth={isConfirmReport ? 'sm' : 'lg'}
      fullWidth
      classes={{
        paper: classes.paper,
      }}
      sx={
        isConfirmReport
          ? {
              '& .MuiDialog-paper': {
                minHeight: 'auto',
                maxHeight: 'calc(100% - 64px)',
              },
            }
          : {}
      }
    >
      {/* === FINAL‐REVIEW HEADER === */}
      {!isConfirmReport && (
        <Stack
          direction={'column'}
          alignItems={'center'}
          gap={1}
          sx={{
            position: 'sticky',
            top: 0,
            backgroundColor: (theme) => theme.palette.background.paper,
            zIndex: 10,
            borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
          }}
        >
          {/* Title + close button */}
          <DialogTitle id="form-dialog-title" fontSize={'24px'} fontWeight={700}>
            Add Water Utility Bill
            <IconButton
              onClick={onClose}
              size="small"
              sx={{ position: 'absolute', right: 8, top: 8 }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>

          {/* 3-segment blue progress bar */}
          {/* Billing progress bar*/}
          {stripeConnect && (
            <Box width="80%" mt={-1} mb={-1}>
              <Stack direction="row" spacing={1}>
                {[0, 1, 2].map((step) => (
                  <Box
                    key={step}
                    flex={1}
                    height={10}
                    borderRadius={3}
                    bgcolor={step <= 2 ? 'primary.main' : '#E0E0E0'}
                  />
                ))}
              </Stack>
            </Box>
          )}
          {/* Billing w/o Stripe progress bar */}
          {!stripeConnect && (
            <Box width="80%" mt={-1} mb={-1}>
              <Stack direction="row" spacing={1}>
                {[0, 1, 2].map((step) => (
                  <Box
                    key={step}
                    flex={1}
                    height={10}
                    borderRadius={3}
                    bgcolor={step <= 2 ? 'primary.main' : '#E0E0E0'}
                  />
                ))}
              </Stack>
            </Box>
          )}

          {/* Subtitle + blurb */}
          {stripeConnect && (
            <Typography variant="subtitle1" fontWeight={500} sx={{ mt: 2 }}>
              Step 3: Final Review
            </Typography>
          )}
          {!stripeConnect && (
            <Typography variant="subtitle1" fontWeight={500} sx={{ mt: 2 }}>
              Step 3: Final Review
            </Typography>
          )}
          <Typography variant="body2" textAlign="center">
            Review how the water utility bill has been distributed across tenants.
          </Typography>
          <Divider sx={{ borderWidth: 1, borderColor: 'grey', width: '100%', mt: 1 }} />
        </Stack>
      )}
      <DialogContent
        ref={targetRef}
        sx={
          isConfirmReport
            ? {
                pt: 3,
                pb: 1,
                minHeight: 'auto',
                '&.MuiDialogContent-root': {
                  minHeight: 'auto',
                  flex: 'initial',
                },
              }
            : {}
        }
      >
        {!isConfirmReport && (
          <>
            <Stack direction="row" justifyContent="space-between" alignItems="stretch" spacing={2}>
              {!isPaymentReport && (
                <Card
                  variant="outlined"
                  sx={
                    effectiveBillsSentTimestamp
                      ? {
                          flexGrow: 1,
                          p: 2,
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'center',
                          borderRadius: '14px',
                        }
                      : {
                          flexGrow: 1,
                          borderRadius: '14px',
                        }
                  }
                >
                  <Box margin={1}>
                    <Stack alignItems="center">
                      <Typography textAlign="center" fontWeight={400} fontSize={'14px'}>
                        Billing Period
                      </Typography>
                      <Typography textAlign="center" fontWeight={700} fontSize={'24px'}>
                        {dayjs.unix(bill.start_time).tz(tz).format('MMM D')} -{' '}
                        {dayjs.unix(bill.end_time).tz(tz).format('MMM D')}
                      </Typography>
                    </Stack>
                  </Box>
                </Card>
              )}
              {!isPaymentReport && (
                <Card
                  variant="outlined"
                  sx={
                    effectiveBillsSentTimestamp
                      ? {
                          flexGrow: 1,
                          p: 2,
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'center',
                          borderRadius: '14px',
                        }
                      : {
                          flexGrow: 1,
                          borderRadius: '14px',
                        }
                  }
                >
                  <Box margin={1}>
                    <Stack alignItems="center">
                      <Typography textAlign="center" fontWeight={400} fontSize={'14px'}>
                        Total Volume
                      </Typography>
                      <Typography textAlign="center" fontWeight={700} fontSize={'24px'}>
                        {bill.volume}
                      </Typography>
                    </Stack>
                  </Box>
                </Card>
              )}
              {!isPaymentReport && (
                <Card
                  variant="outlined"
                  sx={
                    effectiveBillsSentTimestamp
                      ? {
                          flexGrow: 1,
                          p: 2,
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'center',
                          borderRadius: '14px',
                        }
                      : {
                          flexGrow: 1,
                          borderRadius: '14px',
                        }
                  }
                >
                  <Box margin={1}>
                    <Stack alignItems="center">
                      <Typography textAlign="center" fontWeight={400} fontSize={'14px'}>
                        Total Cost
                      </Typography>
                      <Typography textAlign="center" fontWeight={700} fontSize={'24px'}>
                        $
                        {formatNumber(
                          // use updatedBill if you have it, otherwise show bill.cost
                          updatedBill?.cost ?? bill.cost,
                          2,
                        )}
                      </Typography>
                    </Stack>
                  </Box>
                </Card>
              )}
              {/* BOOLEAN DEFINITIONS: */}
              {/* needSendBillButton => sending invoices...? not needed...? */}
              {/* needReportButton => clicking "Send Usage to Tenants"*/}
              {/* needStatus={needSendBillButton || needReportButton ? false : true} => need the Status Column */}
              {/* isConfirmReport => confirm sending usage to tenants */}
              {/* !!effectiveBillsSentTimestamp => when Invoices have been sent; */}
              {/* isPaymentReport => when "View Payment Report" is clicked... that's been deprecated... not needed...? */}
              {/* billingEnabled => billing w/ & w/o stripe */}
              {!isPaymentReport &&
                billingEnabled &&
                !!effectiveBillsSentTimestamp &&
                !needReportButton && (
                  <Card variant="outlined" sx={{ flexGrow: 1, borderRadius: '14px' }}>
                    <Box margin={1}>
                      <Stack alignItems="center">
                        <Typography textAlign="center" fontWeight={400} fontSize={'14px'}>
                          Total Tenant Invoices
                        </Typography>
                        <Typography fontSize="24px" fontWeight={700}>
                          {totalInvoices}
                        </Typography>
                      </Stack>
                      <Stack
                        justifyContent="center"
                        alignItems="center"
                        direction="row"
                        spacing={1}
                        mt={1}
                      >
                        <Chip
                          label={`Open: ${statusCount.open ?? 0}`}
                          variant="outlined"
                          size="small"
                          sx={{ borderColor: 'primary.main', color: 'primary.main' }}
                        />
                        <Chip
                          label={`Paid: ${statusCount.paid ?? 0}`}
                          variant="outlined"
                          size="small"
                          color="success"
                        />
                        <Chip
                          label={`Uncollectible: ${statusCount.uncollectible ?? 0}`}
                          variant="outlined"
                          size="small"
                          color="error"
                        />
                      </Stack>
                    </Box>
                  </Card>
                )}
            </Stack>
          </>
        )}
        {!isConfirmReport && (
          <RenderTable
            bill={updatedBill ?? (bill as IUpdatedBillingData)}
            invoices={localInvoices}
            subscriberById={subscriberById}
            autoDownload={autoDownload}
            title={title}
            onClose={onClose}
            eventDetails={eventDetails}
            timeZone={timeZone}
            _needStatus={needSendBillButton || needReportButton ? false : true}
            needSendBillButton={needSendBillButton}
            billsSentTimestamp={effectiveBillsSentTimestamp}
            stripeConnect={stripeConnect}
            billingEnabled={billingEnabled}
            onInvoicePdfGenerated={handleInvoicePdfGenerated}
            payOnlineUrl={payOnlineUrl}
            paymentInstructions={paymentInstructions}
            dueDateDays={dueDateDays}
            onPaymentSettingsUpdate={onPaymentSettingsUpdate}
            selectedInvoiceIds={selectedInvoiceIds}
            onSelectionChange={setSelectedInvoiceIds}
            showSelectionCheckboxes={localInvoices.length > 0 && !stripeConnect}
            pendingCustomInvoiceIds={pendingCustomInvoiceIds}
            customInvoiceDueDates={customInvoiceDueDates}
            emailSentMap={emailSentMap}
          />
        )}
        {isConfirmReport && (
          <RenderConfirmReport
            propertyName={bill.structure_name}
            address={`${structureAddress.street}, ${structureAddress.state}, ${structureAddress.city}, ${structureAddress.zip}`}
            dateRange={`${formattedStartDate} - ${formattedEndDate}`}
          />
        )}
      </DialogContent>
      <DialogActions
        classes={{ root: classes.dialogActionsRoot }}
        sx={{
          position: 'sticky',
          bottom: 0,
          backgroundColor: (theme) => theme.palette.background.paper,
          zIndex: 10,
          justifyContent: 'space-between',
          px: 2,
          py: 2,
          borderTop: (theme) => `1px solid ${theme.palette.divider}`,
        }}
      >
        {/* LEFT CLOSE; !confirmReport => !confirm send usage */}
        {!isConfirmReport && (
          <SWLoadingButtonOutlined onClick={onClose}>Close</SWLoadingButtonOutlined>
        )}
        {/* Right: Download + Send; !confirmReport => !confirm send usage*/}
        {!isConfirmReport && (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <>
              <SWLoadingButtonOutlined
                aria-controls={isDownloadMenuOpen ? 'download-menu' : undefined}
                aria-haspopup="true"
                onClick={handleDownloadMenuOpen}
                endIcon={<ArrowDropDownIcon />}
                variant="outlined" // or "contained" / your choice
              >
                Download Data
              </SWLoadingButtonOutlined>
              <Menu
                id="download-menu"
                anchorEl={downloadAnchorEl}
                open={isDownloadMenuOpen}
                onClose={handleDownloadMenuClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                <MenuItem onClick={handleDownloadPDF}>Download PDF</MenuItem>
                <MenuItem onClick={handleDownloadCSVMenu}>Download Spreadsheet</MenuItem>
              </Menu>
            </>
            {/* ONLY show "Send Invoices" button if they havent been sent, and have billing enabled */}
            {!effectiveBillsSentTimestamp &&
              needSendBillButton &&
              (billingStatusLoading ? (
                // show a disabled spinner-button while we wait on the API
                <SWLoadingButton loading size="medium">
                  Send Invoices
                </SWLoadingButton>
              ) : billingEnabled && stripeSetupComplete ? (
                // fully onboarded
                <SWLoadingButton onClick={handleOpenSendInvoicesDialog}>
                  Send Invoices
                </SWLoadingButton>
              ) : billingEnabled && !stripeSetupComplete ? (
                // not yet onboarded
                <SWLoadingButton onClick={() => setOpenConnectStripe(true)}>
                  Send Invoices
                </SWLoadingButton>
              ) : null)}
            {needReportButton && !isConfirmReport && (
              <SWLoadingButton onClick={() => setIsConfirmReport(true)}>
                Send Usage Report
              </SWLoadingButton>
            )}
            {/* Show "Generate Invoices" button for non-Stripe accounts with billable units, only if invoices haven't been generated yet */}
            {!stripeConnect &&
              (updatedBill ?? bill)?.units?.length > 0 &&
              localInvoices.length === 0 && (
                <SWLoadingButton
                  onClick={handleOpenBatchGenerateDialog}
                  loading={isBatchGenerating}
                >
                  Generate Invoices
                </SWLoadingButton>
              )}
            {/* Download Selected button - always visible when invoices exist for non-Stripe */}
            {localInvoices.length > 0 && !stripeConnect && (
              <SWLoadingButton
                onClick={handleDownloadSelected}
                disabled={selectedInvoiceIds.size === 0}
              >
                Download Invoices ({selectedInvoiceIds.size})
              </SWLoadingButton>
            )}
            {/* Send Invoices by Email button - visible when invoices exist for non-Stripe */}
            {localInvoices.length > 0 && !stripeConnect && (
              <SWLoadingButton
                onClick={() => setOpenSendEmailDialog(true)}
                loading={isSendingEmails}
                disabled={selectedInvoiceIds.size === 0}
              >
                Send Invoices by Email ({selectedInvoiceIds.size})
              </SWLoadingButton>
            )}
          </Box>
        )}
      </DialogActions>
      {isConfirmReport && (
        <DialogActions
          classes={{ root: classes.dialogActionsRoot }}
          sx={{
            justifyContent: 'space-between',
            px: 2,
            pb: 2,
            pt: 0,
          }}
        >
          <SWLoadingButtonOutlined onClick={() => setIsConfirmReport(false)}>
            Back
          </SWLoadingButtonOutlined>
          <SWLoadingButton onClick={handleSendReport}>
            {loading ? (
              <>
                Loading...
                <CircularProgress size={20} color="inherit" sx={{ marginLeft: '10px' }} />
              </>
            ) : (
              'Confirm Send'
            )}
          </SWLoadingButton>
        </DialogActions>
      )}

      {/* BILLING WITH STRIPE */}
      <Dialog
        open={openConfirmSendInvoices}
        onClose={() => {
          setOpenConfirmSendInvoices(false);
        }}
        aria-labelledby="confirm-send-invoices"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          id="confirm-send-invoices"
          sx={{ fontWeight: 700, fontSize: '24px', textAlign: 'center' }}
        >
          Ready to Send?
          <IconButton
            aria-label="close"
            onClick={() => {
              setOpenConfirmSendInvoices(false);
            }}
            size="small"
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Stack sx={{ px: 3 }}>
            <DialogContentText
              variant="body1"
              color="textSecondary"
              sx={{ mb: 2, px: 3, textAlign: 'left' }}
            >
              Please confirm you want to send invoices to your tenants. SSW invoice PDFs will also
              be generated with the settings below.
            </DialogContentText>

            {/* Days Until Due */}
            <Box sx={{ px: 3, mb: 2 }}>
              <TextField
                fullWidth
                type="number"
                label="Days Until Due"
                value={batchDueDateDays}
                onChange={(e) => {
                  const value = parseInt(e.target.value, 10);
                  if (!isNaN(value) && value >= 1 && value <= 365) {
                    setBatchDueDateDays(value);
                  }
                }}
                inputProps={{ min: 1, max: 365 }}
                size="small"
                helperText={`Invoice due date: ${dayjs()
                  .tz(tz)
                  .add(batchDueDateDays, 'day')
                  .format('MMMM D, YYYY')}`}
              />
            </Box>

            {/* Pay Online URL */}
            <Box sx={{ px: 3, mb: 2 }}>
              <TextField
                fullWidth
                label="Pay Online URL"
                value={batchPayOnlineUrl}
                onChange={(e) => setBatchPayOnlineUrl(e.target.value)}
                placeholder="https://example.com/pay"
                helperText="Link for tenants to pay online (optional)"
                size="small"
              />
            </Box>

            {/* Comments */}
            <Box sx={{ px: 3, mb: 2 }}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Comments"
                value={batchPaymentInstructions}
                onChange={(e) => setBatchPaymentInstructions(e.target.value)}
                placeholder="Enter comments for tenants..."
                helperText="Comments displayed on SSW invoice PDFs (optional)"
                size="small"
              />
            </Box>

            {/* Save as Default Checkbox */}
            {onPaymentSettingsUpdate && (
              <Box sx={{ px: 3 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={batchSaveAsDefault}
                      onChange={(e) => setBatchSaveAsDefault(e.target.checked)}
                    />
                  }
                  label="Save as default for this property"
                />
              </Box>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between', px: 2 }}>
          <SWLoadingButtonOutlined
            sx={{ minWidth: 120 }}
            onClick={() => {
              setOpenConfirmSendInvoices(false);
            }}
          >
            Back
          </SWLoadingButtonOutlined>
          <SWLoadingButton
            sx={{ minWidth: 120 }}
            onClick={async () => {
              // 1) close the "Ready to Send?" prompt
              setOpenConfirmSendInvoices(false);
              // 2) now fire wrapper
              handleSendInvoices();
            }}
            autoFocus
          >
            Confirm
          </SWLoadingButton>
        </DialogActions>
      </Dialog>

      {/* BILLING W/O STRIPE */}
      <Dialog
        open={openConnectStripe}
        onClose={() => {
          setOpenConnectStripe(false);
          setLoadingConnect(false);
        }}
        aria-labelledby="confirm-send-invoices"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          id="confirm-send-invoices"
          sx={{ fontWeight: 700, fontSize: '24px', textAlign: 'center' }}
        >
          Want to Send Invoices?
          <IconButton
            aria-label="close"
            onClick={() => {
              setOpenConnectStripe(false);
            }}
            size="small"
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Stack sx={{ px: 3 }}>
            <DialogContentText
              variant="body1"
              color="textSecondary"
              sx={{ mb: 0, px: 3, textAlign: 'left' }}
            >
              Please finish setting up your Stripe account to gain access to invoices.
            </DialogContentText>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between', px: 2 }}>
          <SWLoadingButtonOutlined
            sx={{ minWidth: 120 }}
            onClick={() => {
              setOpenConnectStripe(false);
              setLoadingConnect(false);
            }}
          >
            Back
          </SWLoadingButtonOutlined>
          <SWLoadingButton
            sx={{ minWidth: 120 }}
            onClick={async () => {
              setLoadingConnect(true);

              try {
                // 1) Create / fetch onboarding URL
                if (!stripeAccountExists) {
                  await createStripeAccount();
                }
                const { url } = await createStripeAccountLink({
                  link_type: STRIPE_ACCOUNT_LINK_TYPE.ACCOUNT_ONBOARDING,
                });

                if (!url) throw new Error('No Stripe URL returned');

                // 2) Open Stripe in a fresh tab
                window.open(url, '_blank');

                // ✅ success toast
                toast.success('Stripe setup opened in a new tab!', {
                  position: 'bottom-right',
                  closeButton: true,
                });

                // 3) Then close dialogs
                onClose();
              } catch (err) {
                console.error('Stripe setup failed', err);
                // ❌ error toast
                toast.error('Failed to set up Stripe. Please try again.', {
                  position: 'bottom-right',
                  closeButton: true,
                });
              } finally {
                setLoadingConnect(false);
              }
            }}
          >
            {loadingConnect
              ? 'Redirecting…'
              : stripeAccountExists
              ? 'Finish Stripe Setup'
              : 'Connect Stripe'}
          </SWLoadingButton>
        </DialogActions>
      </Dialog>

      {/* BATCH GENERATE CUSTOM INVOICES DIALOG */}
      <Dialog
        open={openBatchGenerateDialog}
        onClose={handleCloseBatchGenerateDialog}
        aria-labelledby="batch-generate-invoices-dialog"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="batch-generate-invoices-dialog" sx={{ fontWeight: 700, fontSize: '20px' }}>
          {isSendingStripeInvoices
            ? 'Sending Invoices...'
            : batchGenerationStarted
            ? stripeConnect
              ? 'Invoices Sent'
              : 'Invoices Queued'
            : 'Generate Invoices'}
          <IconButton
            aria-label="close"
            onClick={handleCloseBatchGenerateDialog}
            size="small"
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {isSendingStripeInvoices ? (
            <>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
                <CircularProgress size={48} sx={{ mb: 2 }} />
                <Typography variant="body1" color="text.secondary">
                  Sending Stripe invoices to tenants...
                </Typography>
              </Box>
            </>
          ) : !batchGenerationStarted ? (
            <>
              <DialogContentText sx={{ mb: 3 }}>
                Configure payment settings for all invoices in this billing period. These will
                appear on each generated PDF.
              </DialogContentText>

              {/* Pay Online URL */}
              <TextField
                fullWidth
                label="Pay Online URL"
                value={batchPayOnlineUrl}
                onChange={(e) => {
                  setBatchPayOnlineUrl(e.target.value);
                  setBatchUrlTouched(true);
                }}
                placeholder="https://example.com/pay"
                helperText="Link for tenants to pay online (optional)"
                sx={{ mb: 3 }}
                disabled={isBatchGenerating}
              />

              {/* Comments */}
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Comments"
                value={batchPaymentInstructions}
                onChange={(e) => {
                  setBatchPaymentInstructions(e.target.value);
                  setBatchInstructionsTouched(true);
                }}
                placeholder="Enter comments for tenants..."
                helperText="Comments displayed on each invoice (optional)"
                sx={{ mb: 3 }}
                disabled={isBatchGenerating}
              />

              {/* Days Until Due */}
              <TextField
                fullWidth
                type="number"
                label="Days Until Due"
                value={batchDueDateDays}
                onChange={(e) => {
                  const value = parseInt(e.target.value, 10);
                  if (!isNaN(value) && value >= 1 && value <= 365) {
                    setBatchDueDateDays(value);
                  }
                }}
                inputProps={{ min: 1, max: 365 }}
                helperText={`Invoice due date: ${dayjs()
                  .tz(tz)
                  .add(batchDueDateDays, 'day')
                  .format('MMMM D, YYYY')}`}
                sx={{ mb: 2 }}
                disabled={isBatchGenerating}
              />

              {/* Save as Default Checkbox */}
              {onPaymentSettingsUpdate && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={batchSaveAsDefault}
                      onChange={(e) => setBatchSaveAsDefault(e.target.checked)}
                      disabled={isBatchGenerating}
                    />
                  }
                  label="Save as default for this property"
                />
              )}

              {/* Invoice Count Info */}
              <Box
                sx={{
                  mt: 2,
                  p: 2,
                  backgroundColor: '#E6F7FF',
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <InfoOutlinedIcon sx={{ mr: 1, color: '#096DD9' }} />
                <Typography variant="body2" color="#096DD9">
                  This will generate{' '}
                  {(updatedBill ?? bill)?.billable_units_count ??
                    (updatedBill ?? bill)?.units?.filter((u) => u.is_billable !== false).length ??
                    0}{' '}
                  SSW invoice PDFs. The PDFs will be available for download once processing is
                  complete.
                </Typography>
              </Box>
            </>
          ) : (
            <>
              <DialogContentText sx={{ mb: 2 }}>
                {stripeConnect
                  ? 'Stripe invoices have been sent to your tenants! SSW invoice PDFs are also being generated for your records.'
                  : 'Your SSW invoice PDFs are being generated. This process runs in the background and may take a few moments depending on the number of invoices.'}
              </DialogContentText>

              {/* Progress indicator */}
              {batchGenerationProgress && (
                <Box
                  sx={{
                    p: 2,
                    backgroundColor:
                      batchGenerationProgress.ready >= batchGenerationProgress.total
                        ? '#DFF7DF'
                        : '#E6F7FF',
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    mb: 2,
                  }}
                >
                  {isPolling && batchGenerationProgress.ready < batchGenerationProgress.total && (
                    <CircularProgress size={16} sx={{ mr: 1.5 }} />
                  )}
                  <Typography
                    variant="body2"
                    color={
                      batchGenerationProgress.ready >= batchGenerationProgress.total
                        ? '#198754'
                        : '#096DD9'
                    }
                    sx={{ fontWeight: 500 }}
                  >
                    {batchGenerationProgress.ready >= batchGenerationProgress.total
                      ? `✓ All ${batchGenerationProgress.total} invoice PDFs are ready!`
                      : `${batchGenerationProgress.ready} of ${batchGenerationProgress.total} invoice PDFs ready...`}
                  </Typography>
                </Box>
              )}

              {!batchGenerationProgress && (
                <Box
                  sx={{
                    p: 2,
                    backgroundColor: '#DFF7DF',
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    mb: 2,
                  }}
                >
                  <Typography variant="body2" color="#198754">
                    ✓ {batchCustomInvoices.length} invoice PDFs queued for generation
                  </Typography>
                </Box>
              )}

              <Typography variant="body2" color="text.secondary">
                {batchGenerationProgress &&
                batchGenerationProgress.ready >= batchGenerationProgress.total
                  ? 'All invoices are ready! Close this dialog and select the invoices you want to download.'
                  : 'You can close this dialog and check back later. Select invoices to download when ready.'}
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          {isSendingStripeInvoices ? null : !batchGenerationStarted ? ( // No buttons while sending Stripe invoices - user must wait
            <>
              <SWLoadingButtonOutlined
                onClick={handleCloseBatchGenerateDialog}
                disabled={isBatchGenerating}
              >
                Cancel
              </SWLoadingButtonOutlined>
              <SWLoadingButton onClick={handleBatchGenerate} loading={isBatchGenerating}>
                Generate All Invoices
              </SWLoadingButton>
            </>
          ) : (
            <SWLoadingButton onClick={handleCloseBatchGenerateDialog}>Close</SWLoadingButton>
          )}
        </DialogActions>
      </Dialog>

      {/* SEND INVOICES BY EMAIL DIALOG */}
      <Dialog
        open={openSendEmailDialog}
        onClose={() => !isSendingEmails && setOpenSendEmailDialog(false)}
        aria-labelledby="send-email-dialog"
        maxWidth="md"
        fullWidth
      >
        <DialogTitle id="send-email-dialog">
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Send Invoices by Email</Typography>
            <IconButton
              onClick={() => setOpenSendEmailDialog(false)}
              disabled={isSendingEmails}
              size="small"
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            The following <strong>{selectedEmailRows.length}</strong> invoice(s) will be emailed to
            their billing contacts:
          </DialogContentText>
          {alreadySentCount > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {alreadySentCount === selectedEmailRows.length
                ? 'All selected invoices have already been emailed.'
                : `${alreadySentCount} of ${selectedEmailRows.length} selected invoice(s) have already been emailed.`}{' '}
              Sending again will deliver a duplicate email.
            </Alert>
          )}
          <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 300 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Unit</TableCell>
                  <TableCell>Full Name</TableCell>
                  <TableCell>Email</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {selectedEmailRows.map((row, idx) => (
                  <TableRow key={idx}>
                    <TableCell>{row.unitName}</TableCell>
                    <TableCell>{row.fullName}</TableCell>
                    <TableCell>{row.email}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <SWLoadingButtonOutlined
            onClick={() => setOpenSendEmailDialog(false)}
            disabled={isSendingEmails}
          >
            Cancel
          </SWLoadingButtonOutlined>
          <SWLoadingButton onClick={handleSendInvoiceEmails} loading={isSendingEmails}>
            Send Emails
          </SWLoadingButton>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

interface IRenderConfirmReport {
  propertyName: string;
  address: string;
  dateRange: string;
}

const RenderConfirmReport = (props: IRenderConfirmReport) => {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ textAlign: 'center', mb: 2 }}>
        <Typography variant="h5" fontWeight={'bold'} sx={{ mb: 1 }}>
          Send Usage Reports
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Water usage reports will be sent via email to the tenants for each unit.
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Please check the information below and confirm send.
        </Typography>
      </Box>

      {/* mx needed for Divider to reach full length */}
      <Divider sx={{ mx: -3, mb: 5 }} />

      <Box sx={{ px: 1 }}>
        <Stack spacing={1.5}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Typography fontWeight={'bold'}>Property:</Typography>
            <Typography>{props.propertyName}</Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Typography fontWeight={'bold'}>Address:</Typography>
            <Typography>{props.address}</Typography>
          </Box>
          {/* pb is padding between Date Range and Divider */}
          <Box sx={{ display: 'flex', gap: 1, pb: 5 }}>
            <Typography fontWeight={'bold'}>Date range:</Typography>
            <Typography>{props.dateRange}</Typography>
          </Box>
        </Stack>
      </Box>
    </Box>
  );
};

interface InvoiceWithSubs {
  id: string; // BillingInvoice UUID for custom PDF generation
  unitId: string;
  subscriberId: string;
  stripeInvoiceId: string;
  stripeLastStatus: string;
  stripeInvoicePdfUrl: string | null;
  stripeTotal: number;
  stripeCurrency: string;
  customInvoicePdfUrl: string | null; // Custom invoice PDF URL from S3
}
interface RenderTableProps {
  bill: IUpdatedBillingData;
  invoices: InvoiceWithSubs[];
  subscriberById: Map<string, ISubscriber>;
  autoDownload: 'csv' | 'pdf' | null;
  title: string;
  onClose: Function;
  eventDetails: IEventDetails;
  timeZone: string;
  _needStatus?: boolean;
  billsSentTimestamp: number | null;
  stripeConnect: boolean;
  billingEnabled: boolean;
  needSendBillButton?: boolean;
  forceShowAll?: boolean;
  onInvoicePdfGenerated?: (invoiceId: string, pdfUrl: string) => void;
  payOnlineUrl?: string | null;
  paymentInstructions?: string | null;
  dueDateDays?: number | null;
  onPaymentSettingsUpdate?: (
    payOnlineUrl: string | null,
    paymentInstructions: string | null,
    dueDateDays: number | null,
  ) => Promise<void>;
  // Checkbox selection for batch download
  selectedInvoiceIds?: Set<string>;
  onSelectionChange?: (selectedIds: Set<string>) => void;
  showSelectionCheckboxes?: boolean;
  // Invoices with pending custom invoice generation
  pendingCustomInvoiceIds?: Set<string>;
  // Actual due dates for custom invoices (keyed by billing_invoice_id)
  customInvoiceDueDates?: Map<string, string>;
  // Map of invoice ID → email_sent_at unix timestamp
  emailSentMap?: Map<string, number>;
}

// StatusChip component for displaying invoice status with color (replaced renderStatusColor)
const StatusChip = ({ status }: { status: string }) => {
  let color = '#E0E0E0';
  let textColor = '#5A5A5A';
  // Display "not_created" as "None" since it means no Stripe invoice exists yet
  let displayStatus = status === 'not_created' ? 'None' : status;

  switch (status) {
    case 'not_created':
    case 'None':
      color = '#E0E0E0';
      break;
    case 'draft':
      color = '#D0E9FF';
      break;
    case 'open':
      color = '#FFEFD0';
      break;
    case 'paid':
      color = '#DFF7DF';
      textColor = '#198754';
      break;
    case 'uncollectible':
    case 'uncollectable':
      color = '#FFD6D6';
      textColor = '#D32F2F';
      break;
    case 'void':
      color = '#E4D6FF';
      break;
    default:
      color = '#E0E0E0';
  }
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: color,
        color: textColor,
        borderRadius: '16px',
        fontWeight: 'bold',
        padding: '4px 12px',
        fontSize: '0.875rem',
        textTransform: 'uppercase',
        boxShadow: 1,
        whiteSpace: 'nowrap',
      }}
    >
      {displayStatus.replace('_', ' ').toUpperCase()}
    </Box>
  );
};

const RenderTable = ({
  bill,
  invoices,
  subscriberById,
  autoDownload,
  title,
  onClose,
  eventDetails,
  timeZone,
  _needStatus = true,
  billsSentTimestamp,
  stripeConnect,
  billingEnabled,
  needSendBillButton,
  forceShowAll = false,
  onInvoicePdfGenerated,
  payOnlineUrl,
  paymentInstructions,
  dueDateDays,
  onPaymentSettingsUpdate,
  selectedInvoiceIds,
  onSelectionChange,
  showSelectionCheckboxes = false,
  pendingCustomInvoiceIds = new Set(),
  customInvoiceDueDates = new Map(),
  emailSentMap = new Map(),
}: RenderTableProps) => {
  const [showAllForPDF, setShowAllForPDF] = React.useState(false);
  const { toPDF, targetRef } = useGeneratePDF({
    filename: `${title.replace(/ /g, '_')}_report.pdf`,
  });
  const [alredyExported, setAlredyExported] = useState<boolean>(false);
  const [generatingPdfFor, setGeneratingPdfFor] = useState<string | null>(null);

  const tz = timeZone === '' ? moment.tz.guess() : timeZone;

  // State for Generate Invoice PDF dialog
  const [generatePdfDialogOpen, setGeneratePdfDialogOpen] = useState(false);
  const [pendingInvoice, setPendingInvoice] = useState<{
    billingId: string;
    invoiceId: string;
  } | null>(null);
  const [dialogPayOnlineUrl, setDialogPayOnlineUrl] = useState(payOnlineUrl || '');
  const [dialogPaymentInstructions, setDialogPaymentInstructions] = useState(
    paymentInstructions || '',
  );
  const [dialogUrlTouched, setDialogUrlTouched] = useState(false);
  const [dialogInstructionsTouched, setDialogInstructionsTouched] = useState(false);
  const [saveAsDefault, setSaveAsDefault] = useState(false);
  const [isSavingSettings, setIsSavingSettings] = useState(false);

  // Reset dialog fields when payOnlineUrl/paymentInstructions props change
  React.useEffect(() => {
    setDialogPayOnlineUrl(payOnlineUrl || '');
    setDialogPaymentInstructions(paymentInstructions || '');
  }, [payOnlineUrl, paymentInstructions]);

  // Custom PDF handler that shows all rows
  const handlePDFExport = React.useCallback(async () => {
    setShowAllForPDF(true);
    // Wait for re-render with all rows visible
    await new Promise((resolve) => setTimeout(resolve, 100));
    await toPDF();
    setShowAllForPDF(false);
  }, [toPDF]);

  // Opens dialog instead of directly generating PDF
  const openGeneratePdfDialog = React.useCallback(
    (billingId: string, invoiceId: string) => {
      setPendingInvoice({ billingId, invoiceId });
      // Pre-fill with current defaults (already set via useEffect, but reset for safety)
      setDialogPayOnlineUrl(payOnlineUrl || '');
      setDialogPaymentInstructions(paymentInstructions || '');
      setDialogUrlTouched(false);
      setDialogInstructionsTouched(false);
      setSaveAsDefault(false);
      setGeneratePdfDialogOpen(true);
    },
    [payOnlineUrl, paymentInstructions],
  );

  // Handler for generating custom invoice PDF via API (called after dialog confirmation)
  const handleGenerateCustomPDF = React.useCallback(
    async (
      billingId: string,
      invoiceId: string,
      options?: { pay_online_url?: string; payment_instructions?: string },
    ) => {
      setGeneratingPdfFor(invoiceId);
      try {
        const result = await generateInvoicePDF(billingId, invoiceId, options);
        // Update local state with the new PDF URL
        if (onInvoicePdfGenerated) {
          onInvoicePdfGenerated(invoiceId, result.download_url);
        }
        // Open the download URL in a new tab
        window.open(result.download_url, '_blank');
        toast.success('Invoice PDF generated successfully!', { closeButton: true });
      } catch (error) {
        // Error toast is handled in the API function
        console.error('Failed to generate custom PDF:', error);
      } finally {
        setGeneratingPdfFor(null);
      }
    },
    [onInvoicePdfGenerated],
  );

  // Handler for dialog confirmation
  const handleDialogConfirm = React.useCallback(async () => {
    if (!pendingInvoice) return;

    // If user touched a field, always send it (even if empty) to override defaults
    // If user didn't touch, don't send it so backend can use property defaults
    const options: { pay_online_url?: string; payment_instructions?: string } = {};
    const urlValue = dialogPayOnlineUrl.trim();
    const instructionsValue = dialogPaymentInstructions.trim();

    if (dialogUrlTouched || urlValue) {
      options.pay_online_url = urlValue;
    }
    if (dialogInstructionsTouched || instructionsValue) {
      options.payment_instructions = instructionsValue;
    }

    // If "Save as default" is checked, update the property settings first
    if (saveAsDefault && onPaymentSettingsUpdate) {
      setIsSavingSettings(true);
      try {
        await onPaymentSettingsUpdate(
          urlValue || null,
          instructionsValue || null,
          dueDateDays ?? null,
        );
        toast.success('Payment settings saved as default!', { closeButton: true });
      } catch (error) {
        console.error('Failed to save default settings:', error);
        // Continue with PDF generation even if saving defaults fails
      } finally {
        setIsSavingSettings(false);
      }
    }

    // Close dialog and generate PDF
    setGeneratePdfDialogOpen(false);
    await handleGenerateCustomPDF(pendingInvoice.billingId, pendingInvoice.invoiceId, options);
    setPendingInvoice(null);
  }, [
    pendingInvoice,
    dialogPayOnlineUrl,
    dialogPaymentInstructions,
    dialogInstructionsTouched,
    dialogUrlTouched,
    saveAsDefault,
    onPaymentSettingsUpdate,
    handleGenerateCustomPDF,
    dueDateDays,
  ]);

  // Handler for dialog cancel
  const handleDialogCancel = React.useCallback(() => {
    setGeneratePdfDialogOpen(false);
    setPendingInvoice(null);
  }, []);

  const { trackEvent } = useMixPanel();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const handleChangePage = (_: any, newPage: number) => {
    setPage(newPage);
  };
  const handleChangeRowsPerPage = (e: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  // Checkbox selection handler for individual rows
  const handleSelectRow = React.useCallback(
    (invoiceId: string, checked: boolean) => {
      if (!onSelectionChange || !selectedInvoiceIds) return;
      const newSelection = new Set(selectedInvoiceIds);
      if (checked) {
        newSelection.add(invoiceId);
      } else {
        newSelection.delete(invoiceId);
      }
      onSelectionChange(newSelection);
    },
    [onSelectionChange, selectedInvoiceIds],
  );

  const cellSX = {
    py: 2,
    px: 3,
    borderBottom: 1,
    borderColor: 'divider',
  };

  const UNIT_LABELS: Record<'GALLONS_1K' | 'CCF', string> = {
    GALLONS_1K: '1000 Gallons',
    CCF: 'CCF (Centum Cubic Feet)',
  };

  // Helper function to build fee breakdown for tooltip
  // includeCommonArea: true for Invoice Amount tooltip, false for Unit Cost tooltip
  const buildFeeBreakdown = (
    unitId: string,
    _unitName: string,
    waterCost: number,
    _feeAmount: number,
    isBillable: boolean,
    includeCommonArea: boolean = true,
  ) => {
    const breakdown: { label: string; amount: number }[] = [];

    // Add water usage cost first (unit's own usage)
    breakdown.push({ label: 'Water Usage', amount: waterCost });

    // Add common area usage for billable units when includeCommonArea is true
    if (isBillable && includeCommonArea) {
      const commonAreaUsage = bill.common_area_usage ?? [];
      commonAreaUsage.forEach((ca) => {
        breakdown.push({
          label: `Common area: ${ca.name}`,
          amount: ca.split_cost,
        });
      });
    }

    // Process line items from bill (generateBill API - snake_case)
    // This ensures consistency with bill.units which comes from the same API
    const billLineItems = bill.line_items ?? [];
    const splitFees = billLineItems.filter((item) => item.type === 'SPLIT');
    const flatFees = billLineItems.filter((item) => item.type === 'FLAT');
    const unitFlatFees = billLineItems.filter(
      (item) => item.type === 'UNIT_FLAT' && item.unit_id === unitId,
    );
    // Use billable_units_count from API if available, otherwise fallback to total units
    const billableUnitsCount = bill.billable_units_count ?? bill.units.length;

    // Only show split and flat fees for billable units
    if (isBillable) {
      // Add SPLIT fees (divided by number of billable units)
      splitFees.forEach((fee) => {
        const splitAmount = (fee.cost ?? 0) / billableUnitsCount;
        breakdown.push({ label: fee.description || 'Split Fee', amount: splitAmount });
      });

      // Add FLAT fees (applied to all units)
      flatFees.forEach((fee) => {
        breakdown.push({ label: fee.description || 'Flat Fee', amount: fee.cost ?? 0 });
      });
    }

    // Add UNIT_FLAT fees (specific to this unit) - these can apply to any unit
    unitFlatFees.forEach((fee) => {
      breakdown.push({ label: fee.description || 'Unit-Specific Fee', amount: fee.cost ?? 0 });
    });

    return breakdown;
  };

  useEffect(() => {
    if (autoDownload && !alredyExported) {
      if (autoDownload === 'pdf') {
        setAlredyExported(true);
        handlePDFExport().then(() => {
          trackEvent('Bill Downloaded', eventDetails);
          onClose();
        });
      }
    }
  }, [autoDownload, handlePDFExport, alredyExported, onClose, trackEvent, eventDetails]);

  // Show invoice columns when:
  // 1. Stripe invoices have been sent (stripeConnect && billsSentTimestamp)
  // 2. OR invoices exist (for custom invoice PDF generation)
  const hasInvoices = invoices.length > 0;
  const showInvoiceColumns = (stripeConnect && !!billsSentTimestamp) || hasInvoices;
  // Status column only shown for Stripe-connected accounts (non-Stripe always shows "NONE")
  const showStatusColumn = stripeConnect && !!billsSentTimestamp;
  // Due date column shown for Stripe accounts with bills sent, OR non-Stripe accounts with generated invoices
  const hasCustomInvoicesWithDueDates = customInvoiceDueDates.size > 0;
  const showDueDateColumn = showStatusColumn || hasCustomInvoicesWithDueDates;
  const showEmailSentColumn = !stripeConnect && emailSentMap.size > 0;

  // Calculate total common area split cost (only applies to billable units)
  const commonAreaUsage = bill.common_area_usage ?? [];
  const totalCommonAreaSplitCost = commonAreaUsage.reduce((sum, ca) => sum + ca.split_cost, 0);

  // fall back to bill.units if there are no invoices yet
  // Show invoice data when invoices exist (for both Stripe and custom invoices)
  const rows = bill.units
    .flatMap((unit) => {
      // invoices come from bills API which converts to camelCase
      const unitInvoices = invoices.filter((inv) => inv.unitId === unit.id);
      const isBillable = unit.is_billable ?? true; // Default to true for backwards compatibility

      // unit_cost already includes: common area split + split fees + flat fees + unit flat fees
      // Invoice amount = water usage + unit_cost (this is what appears on the invoice)
      const invoiceAmount = (unit.unit_cost_meter ?? 0) + (unit.unit_cost ?? 0);

      // Unit cost = invoice amount minus common area split (for billable units)
      // This represents just the unit's water usage + fees, without common area charges
      const unitCost = isBillable ? invoiceAmount - totalCommonAreaSplitCost : invoiceAmount;

      // feeAmount includes common area, so we need feeAmountWithoutCommonArea for Unit Cost tooltip
      const feeAmountWithCommonArea = unit.unit_cost ?? 0;
      const feeAmountWithoutCommonArea = isBillable
        ? feeAmountWithCommonArea - totalCommonAreaSplitCost
        : feeAmountWithCommonArea;

      if (unitInvoices.length > 0) {
        return unitInvoices.map((inv) => {
          const subscriber = inv.subscriberId ? subscriberById.get(inv.subscriberId) : undefined;
          // Use actual custom invoice due date if available, otherwise calculate from billsSentTimestamp
          const customDueDate = customInvoiceDueDates.get(inv.id);
          const dueDate = getDueDate(
            customDueDate,
            billsSentTimestamp ?? bill.end_time,
            dueDateDays ?? 21,
            timeZone,
          );
          return {
            id: inv.stripeInvoiceId || inv.id,
            invoiceId: inv.id as string | null, // BillingInvoice UUID for custom PDF generation
            billingId: bill.billing_id,
            unitId: unit.id,
            unitName: unit.name,
            tenant: subscriber ? `${subscriber.first_name} ${subscriber.last_name}` : '—',
            usage: unit.unit_total_volume,
            volumeUnit: eventDetails.VolumeUnit,
            unitCost, // Unit's water usage + fees (without common area)
            invoiceAmount: inv.stripeTotal ?? invoiceAmount, // Full invoice amount (with common area)
            waterCost: unit.unit_cost_meter ?? 0,
            feeAmount: feeAmountWithoutCommonArea, // Fees without common area (for Unit Cost tooltip)
            dueDate: dueDate as number | null,
            status: inv.stripeLastStatus,
            pdfUrl: inv.stripeInvoicePdfUrl,
            customPdfUrl: inv.customInvoicePdfUrl,
            isBillable,
            emailSentAt: emailSentMap.get(inv.id) ?? null,
            // TODO: Uncomment when stripe_invoice_url feature is ready for deployment
            // stripeInvoiceUrl: inv.stripeInvoiceUrl,
          };
        });
      } else {
        return [
          {
            id: unit.id,
            invoiceId: null as string | null, // No invoice yet
            billingId: bill.billing_id,
            unitId: unit.id,
            unitName: unit.name,
            tenant: '-',
            usage: unit.unit_total_volume,
            volumeUnit: eventDetails.VolumeUnit,
            unitCost, // Unit's water usage + fees (without common area)
            invoiceAmount, // Full invoice amount (with common area)
            waterCost: unit.unit_cost_meter ?? 0,
            feeAmount: feeAmountWithoutCommonArea, // Fees without common area (for Unit Cost tooltip)
            dueDate: null as number | null, // No invoice yet, so no due date
            status: 'None',
            pdfUrl: null as string | null,
            customPdfUrl: null as string | null,
            isBillable,
            emailSentAt: null as number | null,
            // TODO: Uncomment when stripe_invoice_url feature is ready for deployment
            // stripeInvoiceUrl: null,
          },
        ];
      }
    })
    // sort by unitName, then tenant name (if unitName is the same)
    .sort((a, b) =>
      a.unitName === b.unitName
        ? naturalCompare(a.tenant, b.tenant)
        : naturalCompare(a.unitName, b.unitName),
    );

  // after sorting, count the number of rows per Unit (so that we can have that value span multiple rows)
  const spanCounts = rows.reduce<Record<string, number>>((acc, r) => {
    acc[r.unitName] = (acc[r.unitName] || 0) + 1;
    return acc;
  }, {});
  const start = page * rowsPerPage;
  // Show all rows if forceShowAll or showAllForPDF is true, otherwise use pagination
  const shouldShowAll = forceShowAll || showAllForPDF;
  const displayRows = shouldShowAll ? rows : rows.slice(start, start + rowsPerPage);

  const rowsPerPageOptions = [5, 10, 20, { label: 'All', value: rows.length }];

  type RowType = (typeof rows)[number];

  // Compute selectable rows (only rows with invoiceId can be selected for PDF download)
  const selectableRows = rows.filter((r) => r.invoiceId !== null);
  const selectableRowIds = selectableRows.map((r) => r.invoiceId as string);
  const allSelected =
    selectableRowIds.length > 0 && selectedInvoiceIds
      ? selectableRowIds.every((id) => selectedInvoiceIds.has(id))
      : false;
  const someSelected = selectedInvoiceIds
    ? selectableRowIds.some((id) => selectedInvoiceIds.has(id)) && !allSelected
    : false;

  // Handler for select all checkbox (now we have access to rows)
  const handleSelectAllRows = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!onSelectionChange) return;
    if (event.target.checked) {
      onSelectionChange(new Set(selectableRowIds));
    } else {
      onSelectionChange(new Set());
    }
  };

  return (
    <div ref={targetRef}>
      <TableContainer
        component={Paper}
        sx={{
          boxShadow: 'none',
          borderTop: 1,
          borderBottom: 1,
          borderColor: 'divider',
          mt: 2,
        }}
      >
        <Table size="medium" aria-label="simple table">
          <TableHead>
            <TableRow>
              {/* Checkbox column for batch selection */}
              {showSelectionCheckboxes && (
                <TableCell sx={{ ...cellSX, width: 50 }} align="center" padding="checkbox">
                  <Checkbox
                    indeterminate={someSelected}
                    checked={allSelected}
                    onChange={handleSelectAllRows}
                    inputProps={{ 'aria-label': 'select all invoices' }}
                  />
                </TableCell>
              )}
              <TableCell sx={cellSX} align="center">
                Unit Name
              </TableCell>
              <TableCell sx={cellSX} align="center">
                Water Usage
              </TableCell>
              {showInvoiceColumns && (
                <TableCell sx={cellSX} align="center">
                  Tenant
                </TableCell>
              )}
              <TableCell sx={cellSX} align="center">
                Volume Unit
              </TableCell>
              <TableCell sx={cellSX} align="center">
                <Box
                  sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}
                >
                  Unit Cost
                  <Tooltip
                    title="Water usage cost plus any fees for this unit (excludes common area charges)."
                    arrow
                    placement="top"
                  >
                    <IconButton size="small" sx={{ p: 0, ml: 0.5 }}>
                      <InfoOutlinedIcon
                        sx={{
                          fontSize: '17px',
                          color: '#555',
                        }}
                      />
                    </IconButton>
                  </Tooltip>
                </Box>
              </TableCell>
              <TableCell sx={cellSX} align="center">
                <Box
                  sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}
                >
                  Invoice Amount
                  <Tooltip
                    title="Total invoice amount including unit cost plus split common area charges. This is what appears on the tenant's invoice."
                    arrow
                    placement="top"
                  >
                    <IconButton size="small" sx={{ p: 0, ml: 0.5 }}>
                      <InfoOutlinedIcon
                        sx={{
                          fontSize: '17px',
                          color: '#555',
                        }}
                      />
                    </IconButton>
                  </Tooltip>
                </Box>
              </TableCell>
              {/* Due Date column shown for Stripe accounts OR non-Stripe with generated invoices */}
              {showDueDateColumn && (
                <TableCell sx={cellSX} align="center">
                  Due Date
                </TableCell>
              )}
              {showEmailSentColumn && (
                <TableCell sx={cellSX} align="center">
                  Invoice Sent
                </TableCell>
              )}
              {/* Status column only shown for Stripe-connected accounts */}
              {showStatusColumn && (
                <TableCell sx={cellSX} align="center">
                  Status
                </TableCell>
              )}
              {showInvoiceColumns && (
                <TableCell sx={cellSX} align="center">
                  Invoice
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          {/* TABLE BODY HANDLING PRORATED UNITS W/ MULTIPLE TENANTS */}
          <TableBody>
            {displayRows.map((row: RowType, idx) => {
              // figure out if this is the first time we've seen this unit in the full `rows` array
              // When showing all rows, idx is the actual index
              const globalIdx = shouldShowAll ? idx : start + idx;
              const prev = rows[globalIdx - 1];
              const isFirstOfUnit = globalIdx === 0 || row.unitName !== prev.unitName;
              const rowSpan = spanCounts[row.unitName];

              return (
                <TableRow key={row.id}>
                  {/* Checkbox cell for batch selection */}
                  {showSelectionCheckboxes && (
                    <TableCell align="center" padding="checkbox">
                      {row.invoiceId ? (
                        <Checkbox
                          checked={selectedInvoiceIds?.has(row.invoiceId) ?? false}
                          onChange={(e) => handleSelectRow(row.invoiceId!, e.target.checked)}
                          inputProps={{ 'aria-label': `select invoice for ${row.unitName}` }}
                        />
                      ) : (
                        <Checkbox disabled />
                      )}
                    </TableCell>
                  )}
                  {isFirstOfUnit && (
                    <>
                      <TableCell rowSpan={rowSpan} align="center">
                        {row.unitName}
                      </TableCell>
                      <TableCell rowSpan={rowSpan} align="center">
                        {formatNumber(row.usage, 3)}
                      </TableCell>
                    </>
                  )}
                  {/* Render TENANTS when invoices exist */}
                  {showInvoiceColumns && <TableCell align="center">{row.tenant}</TableCell>}
                  {/* render every time */}
                  <TableCell align="center">
                    {UNIT_LABELS[row.volumeUnit as 'GALLONS_1K' | 'CCF']}
                  </TableCell>

                  {/* Unit Cost column - water usage + fees (excludes common area) */}
                  <TableCell align="center">
                    <Tooltip
                      title={
                        <Box>
                          {buildFeeBreakdown(
                            row.unitId,
                            row.unitName,
                            row.waterCost,
                            row.feeAmount,
                            row.isBillable,
                            false, // exclude common area
                          ).map((item, idx) => (
                            <Box
                              key={idx}
                              sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                minWidth: 150,
                              }}
                            >
                              <Typography variant="caption" sx={{ mr: 2 }}>
                                {item.label}:
                              </Typography>
                              <Typography variant="caption">
                                ${formatNumber(item.amount, 2)}
                              </Typography>
                            </Box>
                          ))}
                          <Box
                            sx={{
                              borderTop: 1,
                              borderColor: 'divider',
                              mt: 0.5,
                              pt: 0.5,
                              display: 'flex',
                              justifyContent: 'space-between',
                            }}
                          >
                            <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                              Total:
                            </Typography>
                            <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                              ${formatNumber(row.unitCost, 2)}
                            </Typography>
                          </Box>
                        </Box>
                      }
                      arrow
                      placement="left"
                    >
                      <Typography
                        component="span"
                        sx={{
                          cursor: 'help',
                          borderBottom: '1px dotted rgba(0, 0, 0, 0.3)',
                          '&:hover': {
                            borderBottomColor: 'primary.main',
                          },
                        }}
                      >
                        ${formatNumber(row.unitCost, 2)}
                      </Typography>
                    </Tooltip>
                  </TableCell>

                  {/* Invoice Amount column - full amount including common area split */}
                  <TableCell align="center">
                    <Tooltip
                      title={
                        <Box>
                          {buildFeeBreakdown(
                            row.unitId,
                            row.unitName,
                            row.waterCost,
                            row.feeAmount,
                            row.isBillable,
                            true, // include common area
                          ).map((item, idx) => (
                            <Box
                              key={idx}
                              sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                minWidth: 150,
                              }}
                            >
                              <Typography variant="caption" sx={{ mr: 2 }}>
                                {item.label}:
                              </Typography>
                              <Typography variant="caption">
                                ${formatNumber(item.amount, 2)}
                              </Typography>
                            </Box>
                          ))}
                          <Box
                            sx={{
                              borderTop: 1,
                              borderColor: 'divider',
                              mt: 0.5,
                              pt: 0.5,
                              display: 'flex',
                              justifyContent: 'space-between',
                            }}
                          >
                            <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                              Total:
                            </Typography>
                            <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                              ${formatNumber(row.invoiceAmount, 2)}
                            </Typography>
                          </Box>
                        </Box>
                      }
                      arrow
                      placement="left"
                    >
                      <Typography
                        component="span"
                        sx={{
                          cursor: 'help',
                          borderBottom: '1px dotted rgba(0, 0, 0, 0.3)',
                          '&:hover': {
                            borderBottomColor: 'primary.main',
                          },
                        }}
                      >
                        ${formatNumber(row.invoiceAmount, 2)}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  {/* Due Date column shown for Stripe accounts OR non-Stripe with generated invoices */}
                  {showDueDateColumn && (
                    <TableCell align="center">
                      {row.dueDate ? dayjs(row.dueDate).format('MM/DD/YYYY') : '—'}
                    </TableCell>
                  )}
                  {showEmailSentColumn && (
                    <TableCell align="center">
                      {row.emailSentAt
                        ? dayjs.unix(row.emailSentAt).tz(tz).format('MM/DD/YYYY h:mm A')
                        : '—'}
                    </TableCell>
                  )}

                  {/* Render STATUS only for Stripe-connected accounts */}
                  {showStatusColumn && (
                    <TableCell align="center">
                      <StatusChip status={row.status} />
                    </TableCell>
                  )}
                  {/* Render INVOICE column when invoices exist (Stripe or custom) */}
                  {showInvoiceColumns && (
                    <TableCell align="center">
                      <Stack direction="row" spacing={0.5} justifyContent="center">
                        {/* Stripe Invoice PDF */}
                        {row.pdfUrl ? (
                          <Tooltip title="Download Stripe Invoice" arrow>
                            <IconButton
                              onClick={() => row.pdfUrl && (window.location.href = row.pdfUrl)}
                              size="small"
                            >
                              <SimCardDownloadOutlinedIcon />
                            </IconButton>
                          </Tooltip>
                        ) : null}
                        {/* Custom Invoice PDF - show download if already generated */}
                        {row.customPdfUrl ? (
                          <Tooltip title="Download SSW Invoice" arrow>
                            <IconButton
                              onClick={() => window.open(row.customPdfUrl!, '_blank')}
                              size="small"
                            >
                              <SimCardDownloadOutlinedIcon color="primary" />
                            </IconButton>
                          </Tooltip>
                        ) : row.invoiceId ? (
                          <Tooltip
                            title={
                              pendingCustomInvoiceIds.has(row.invoiceId)
                                ? 'SSW Invoice generating...'
                                : 'Generate SSW Invoice'
                            }
                            arrow
                          >
                            <span>
                              <IconButton
                                onClick={() => openGeneratePdfDialog(row.billingId, row.invoiceId!)}
                                size="small"
                                disabled={
                                  generatingPdfFor === row.invoiceId ||
                                  pendingCustomInvoiceIds.has(row.invoiceId)
                                }
                              >
                                {generatingPdfFor === row.invoiceId ||
                                pendingCustomInvoiceIds.has(row.invoiceId) ? (
                                  <CircularProgress size={20} />
                                ) : (
                                  <DescriptionOutlinedIcon />
                                )}
                              </IconButton>
                            </span>
                          </Tooltip>
                        ) : null}
                        {/* Show placeholder if no invoice at all */}
                        {!row.pdfUrl && !row.customPdfUrl && !row.invoiceId && (
                          <Typography>-</Typography>
                        )}
                      </Stack>
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        {!shouldShowAll && (
          <TablePagination
            component="div"
            rowsPerPageOptions={rowsPerPageOptions}
            count={rows.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        )}
      </TableContainer>
      {/* WHEN: View Bill, After Creating a Bill, and "Send Invoices to Tenants" */}
      {/* BOOLEANS: billing is enabled, have the send invoice button, invoices arent sent */}
      {billingEnabled && needSendBillButton && !billsSentTimestamp && (
        <Box
          sx={{
            mt: 2,
            px: 1,
            py: 1,
            display: 'flex',
            alignItems: 'center',
            backgroundColor: '#E6F7FF', // blue background
            color: '#096DD9', // dark-blue text
            borderRadius: 3,
          }}
        >
          <InfoOutlinedIcon fontSize="small" sx={{ mr: 0.7 }} />
          <Typography component="span">
            Make sure your tenant contact information is up to date before sending invoices!
          </Typography>
        </Box>
      )}
      {/* WHEN: View Bill, After Creating a Bill, and "Send Invoices to Tenants" */}
      {/* BOOLEANS: billing is enabled, have the send invoice button, invoices arent sent */}
      {billingEnabled && needSendBillButton && !billsSentTimestamp && (
        <Box
          sx={{
            mt: 2,
            px: 1,
            py: 1,
            display: 'flex',
            alignItems: 'center',
            backgroundColor: '#E6F7FF', // blue background
            color: '#096DD9', // dark-blue text
            borderRadius: 3,
          }}
        >
          <InfoOutlinedIcon fontSize="small" sx={{ mr: 0.7 }} />
          <Typography component="span">
            You've been granted early access to SimpleSUB Billing. Creating invoices, sending
            invoices, and payment collection are currently in beta.
          </Typography>
        </Box>
      )}

      {/* Generate SSW Invoice PDF Dialog */}
      <Dialog
        open={generatePdfDialogOpen}
        onClose={handleDialogCancel}
        aria-labelledby="generate-invoice-pdf-dialog"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="generate-invoice-pdf-dialog" sx={{ fontWeight: 700, fontSize: '20px' }}>
          Generate SSW Invoice PDF
          <IconButton
            aria-label="close"
            onClick={handleDialogCancel}
            size="small"
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 3 }}>
            Configure payment settings for this invoice. These will appear on the generated PDF.
          </DialogContentText>

          {/* Pay Online URL */}
          <TextField
            fullWidth
            label="Pay Online URL"
            value={dialogPayOnlineUrl}
            onChange={(e) => {
              setDialogPayOnlineUrl(e.target.value);
              setDialogUrlTouched(true);
            }}
            placeholder="https://example.com/pay"
            helperText="Link for tenants to pay online (optional)"
            sx={{ mb: 3 }}
            disabled={isSavingSettings || generatingPdfFor !== null}
          />

          {/* Comments */}
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Comments"
            value={dialogPaymentInstructions}
            onChange={(e) => {
              setDialogPaymentInstructions(e.target.value);
              setDialogInstructionsTouched(true);
            }}
            placeholder="Enter comments for tenants..."
            helperText="Comments displayed on the invoice (optional)"
            sx={{ mb: 2 }}
            disabled={isSavingSettings || generatingPdfFor !== null}
          />

          {/* Save as Default Checkbox */}
          {onPaymentSettingsUpdate && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={saveAsDefault}
                  onChange={(e) => setSaveAsDefault(e.target.checked)}
                  disabled={isSavingSettings || generatingPdfFor !== null}
                />
              }
              label="Save as default for this property"
            />
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <SWLoadingButtonOutlined
            onClick={handleDialogCancel}
            disabled={isSavingSettings || generatingPdfFor !== null}
          >
            Cancel
          </SWLoadingButtonOutlined>
          <SWLoadingButton
            onClick={handleDialogConfirm}
            loading={isSavingSettings || generatingPdfFor !== null}
          >
            Generate PDF
          </SWLoadingButton>
        </DialogActions>
      </Dialog>
    </div>
  );
};
